import React, { useState, useCallback } from 'react';
import { BRAND } from '../../branding';
import { DELIVERY_TRACKING_ROLES } from '../../constants/deliveryTracking';
import { usePharmacyBranding } from '../../hooks/usePharmacyBranding';
import { useDefaultLandingPage } from '../../hooks/useDefaultLandingPage';
import { landingNoticeState } from '../Auth/LandingNotice';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListSubheader,
  Badge,
  Tooltip,
  Paper,
  alpha,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  AddCircleOutline as AddIcon,
  Dashboard as DashboardIcon,
  Chat as ChatIcon,
  BugReport,
  HelpOutline,
  AdminPanelSettings,
  Description,
  ListAlt,
  LocalShipping,
  Inventory,
  Assignment,
  Business as BusinessIcon,
  Medication as MedicationIcon,
  Sms as SmsIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAuth0 } from '@auth0/auth0-react';
import { PharmacyContextCard } from '../PharmacySelector';
import * as Sentry from '@sentry/react';
import { useChatwoot } from '../../hooks/useChatwoot';
import api from '../../services/api';
import { RxSearchDialog, RxSearchResult } from '../rx';
import { useUserAvatar } from '../../hooks/queries';
import { useTaskQueueStore } from '../../stores/taskQueueStore';
import { useGlobalSearchContext } from '../GlobalSearch';
import { useGlobalPatientSearch } from '../PatientSearch/GlobalPatientSearchProvider';
import { PatientInteractionsMenu } from './PatientInteractionsMenu';
import {
  FIND_PATIENT_SHORTCUT,
  SEARCH_RX_SHORTCUT,
  TASK_QUEUE_SHORTCUT,
  formatShortcut,
  shortcutForPath,
} from '../../constants/keyboardShortcuts';
import { useShiftShortcuts } from '../../hooks/useKeyboardShortcuts';
import { ClinicModuleGate } from '../common/ClinicModuleGate';
import { visibleTopbarQuickActions, TOPBAR_QUICK_ACTION_KEYS, type TopbarQuickActionKey } from './topbarQuickActions';
import { useBlocksHidden } from '../../hooks/queries/usePharmacyVisibility';

interface ModernTopbarProps {
  onDrawerToggle: () => void;
  drawerWidth: number;
  topbarHeight: number;
  sidebarHidden?: boolean;
}

export const ModernTopbar: React.FC<ModernTopbarProps> = ({
  onDrawerToggle,
  drawerWidth,
  topbarHeight,
  sidebarHidden = false
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const branding = usePharmacyBranding();
  const { logout, hasCustomRole, hasRole, customRole } = useAuth();
  const isPharmacyAdmin = !customRole
    ? hasRole('admin')
    : hasCustomRole('pharmacy_admin') || hasCustomRole('wally_dev_admin');
  // Delivery Tracking & Clinic Management: visible to admins + specific operational roles
  const canSeeOperations = !customRole
    ? hasRole('admin')
    : DELIVERY_TRACKING_ROLES.some((role) => hasCustomRole(role));
  const { user } = useAuth0();
  const { toggle } = useChatwoot();
  const openTaskQueue = useTaskQueueStore((s) => s.open);
  const { openSearch } = useGlobalSearchContext();
  const { openPatientSearch } = useGlobalPatientSearch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [rxSearchOpen, setRxSearchOpen] = useState(false);
  const [quickActionsAnchor, setQuickActionsAnchor] = useState<null | HTMLElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Use React Query for avatar - cached globally, fetched once per session
  const { data: avatarData } = useUserAvatar();
  const avatarUrl = avatarData?.avatarUrl || null;

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  const handleQuickActionsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setQuickActionsAnchor(event.currentTarget);
  };

  const handleQuickActionsClose = () => {
    setQuickActionsAnchor(null);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // PRX-40 review: shared hook instead of a duplicate localStorage read —
  // a not-yet-ready path is simply DEFAULT_LANDING_PAGE, which is fine here
  // since this is a one-off click, not the app-boot redirect.
  // PRX-124: and the same notice when the role's page isn't open to the user.
  const { path: defaultLandingPage, notice: landingNotice } = useDefaultLandingPage();

  const getUserName = () => {
    if (!user) return 'User';
    
    // Try to get the user's name from various possible fields
    const displayName = 
      user.name || 
      user.given_name || 
      user.family_name ? `${user.given_name || ''} ${user.family_name || ''}`.trim() :
      user.nickname || 
      user['https://phox.pharmaguide.com/user_metadata']?.firstName || 
      user['https://phox.pharmaguide.com/user_metadata']?.name ||
      user.email?.split('@')[0] || 
      'User';
    
    // Get first name only for greeting
    return displayName.split(' ')[0];
  };

  const getUserInitial = () => {
    const name = getUserName();
    return name.charAt(0).toUpperCase();
  };

  const getEnvironmentConfig = () => {
    const envHeader = import.meta.env.VITE_ENV_HEADER?.toLowerCase();
    // const envHeader: string = "uat";

    switch (envHeader) {
      case 'prod':
        return {
          label: 'PROD',
          color: '#ef4444' as const, // Red
          bgColor: alpha('#ef4444', 0.15),
        };
      case 'uat':
        return {
          label: 'UAT',
          color: '#f97316' as const, // Orange
          bgColor: alpha('#f97316', 0.15),
        };
      case 'qa':
        return {
          label: 'QA',
          color: '#3b82f6' as const, // Blue
          bgColor: alpha('#3b82f6', 0.15),
        };
      default:
        return null;
    }
  };

  const envConfig = getEnvironmentConfig();

  // Handle Rx search selection. Since GP-1399 the dialog itself resolves the Rx
  // to its order and navigates there, so nothing is left to do here but make
  // sure the dialog is shut (it also calls onClose on its way out).
  const handleRxSelect = useCallback((_result: RxSearchResult) => {
    setRxSearchOpen(false);
  }, []);

  // GP-482: every ribbon button carries its hotkey in the tooltip — the keys
  // themselves come from the shared registry so the hint can never drift from
  // what's actually bound. Search Rx is bound HERE rather than in AppShortcuts
  // because the ribbon owns the dialog's open state.
  const allQuickActions: Array<{ key: TopbarQuickActionKey; icon: React.ReactNode; label: string; shortcut: string | undefined; action: () => void }> = [
    { key: 'operations_dashboard', icon: <DashboardIcon />, label: 'Operations Dashboard', shortcut: shortcutForPath('/order-fulfillment/operations-dashboard'), action: () => navigate('/order-fulfillment/operations-dashboard') },
    // PRX-15: one search for Rx numbers, patients and orders — the palette
    // resolves an Rx number in the pharmacy system too. Rafik: "I don't find
    // it very intuitive that you have a Search RX and Search Patient."
    { key: 'search', icon: <SearchIcon />, label: 'Search Rx, patient or order', shortcut: formatShortcut(SEARCH_RX_SHORTCUT.key), action: () => openSearch() },
    { key: 'new_order', icon: <AddIcon />, label: 'New Order', shortcut: shortcutForPath('/order-fulfillment/Phox-entry'), action: () => navigate('/order-fulfillment/Phox-entry') },
    { key: 'all_orders', icon: <ListAlt />, label: 'All Orders', shortcut: shortcutForPath('/order-fulfillment/all-orders'), action: () => navigate('/order-fulfillment/all-orders') },
    { key: 'task_queue', icon: <Assignment />, label: 'Task Queue', shortcut: formatShortcut(TASK_QUEUE_SHORTCUT.key), action: () => openTaskQueue() },
  ];
  // PRX-83 -> PRX-54: Operations dashboard, All orders and Task queue duplicate
  // the left sidebar, and Search duplicates the top-bar search box — the
  // per-pharmacy visibility preference now owns which quick actions render
  // (topbar.action.* in visibility/visibilityCatalogue.ts); an empty record
  // reproduces the same defaults. One subscription resolves all five keys.
  const topbarVisibility = useBlocksHidden(
    TOPBAR_QUICK_ACTION_KEYS.map((key) => `topbar.action.${key}`),
  );
  const isTopbarActionHidden = (blockKey: string) => topbarVisibility[blockKey] ?? false;
  const quickActions = visibleTopbarQuickActions(allQuickActions, isTopbarActionHidden);

  useShiftShortcuts({
    // PRX-15: the old Search Rx shortcut opens the unified search.
    [SEARCH_RX_SHORTCUT.key]: () => openSearch(),
  });

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: '100%',
        height: topbarHeight,
        // Dark mode: a deep ink-blue surface instead of the bright brand
        // gradient — the light gradient glares against dark content.
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #16202e 0%, #101722 100%)'
          : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        backdropFilter: 'blur(10px)',
        borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
        zIndex: theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ minHeight: topbarHeight, height: topbarHeight, px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Hamburger Menu Button */}
        <Tooltip title={sidebarHidden ? 'Show sidebar (Ctrl+B)' : 'Hide sidebar (Ctrl+B)'}>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={onDrawerToggle}
            sx={{ mr: { xs: 0.5, sm: 1, md: 2 } }}
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>

        {/* Logo - Hidden on mobile */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: 1.5,
            mr: { sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
            onClick={() => navigate(defaultLandingPage, { state: landingNoticeState(landingNotice) })}
          >
            <img
              src={branding.logoSmall}
              alt={BRAND.name}
              width={146}
              height={32}
              style={{
                height: '32px',
                width: 'auto',
                objectFit: 'contain',
                display: 'block',
                filter: 'brightness(0) invert(1)', // Makes logo white
              }}
            />
          </Box>

          {/* Environment Indicator */}
          {envConfig && (
            <Chip
              label={envConfig.label}
              size="small"
              sx={{
                height: '24px',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.5px',
                color: envConfig.color,
                backgroundColor: envConfig.bgColor,
                border: `1.5px solid ${envConfig.color}`,
                '& .MuiChip-label': {
                  px: 1.5,
                },
                boxShadow: `0 0 12px ${alpha(envConfig.color, 0.3)}`,
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    boxShadow: `0 0 12px ${alpha(envConfig.color, 0.3)}`,
                  },
                  '50%': {
                    boxShadow: `0 0 20px ${alpha(envConfig.color, 0.5)}`,
                  },
                },
              }}
            />
          )}
        </Box>

        {/* Greeting and Search */}
        {/* minWidth: 0 lets this middle section actually compress — without it
            the fixed-width search pushed the avatar off-screen at ~1300px. */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2, md: 3 } }}>
          <Box sx={{ display: { xs: 'none', lg: 'block' }, whiteSpace: 'nowrap' }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 500 }}>
              {getGreeting()}, {getUserName()}
            </Typography>
            <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.7) }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </Typography>
          </Box>

          {/* Global Search Trigger - Full bar on sm+, icon button on mobile */}
          {isMobile ? (
            <Tooltip title="Search (Ctrl+K)">
              <IconButton
                onClick={openSearch}
                sx={{
                  color: 'white',
                  bgcolor: alpha(theme.palette.common.white, 0.1),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.2),
                  },
                }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Paper
              onClick={openSearch}
              sx={{
                p: '6px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: { sm: 180, md: 260, lg: 320 },
                minWidth: 120,
                flexShrink: 1,
                bgcolor: alpha(theme.palette.common.white, 0.12),
                backdropFilter: 'blur(10px)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.22),
                },
              }}
              elevation={0}
            >
              <SearchIcon sx={{ color: alpha(theme.palette.common.white, 0.7), fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{ flex: 1, color: alpha(theme.palette.common.white, 0.6) }}
              >
                Search...
              </Typography>
              {!isTablet && (
                <Chip
                  label="Ctrl+K"
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: 11,
                    color: alpha(theme.palette.common.white, 0.7),
                    borderColor: alpha(theme.palette.common.white, 0.3),
                  }}
                  variant="outlined"
                />
              )}
            </Paper>
          )}
        </Box>

        {/* Right Side Actions — never shrink: the avatar and pharmacy selector
            must stay fully visible at every width. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexShrink: 0 }}>
          {/* Quick Actions - Desktop Only */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 1 }}>
            {quickActions.map((action) => (
              <Tooltip
                key={action.key}
                title={action.shortcut ? `${action.label} — ${action.shortcut}` : action.label}
              >
                <IconButton
                  sx={{
                    color: 'white',
                    bgcolor: alpha(theme.palette.common.white, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.common.white, 0.2),
                    },
                  }}
                  onClick={action.action}
                >
                  {action.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>

          {/* Patient history (GP-1508) — sits with Find Patient because it is the
              same job: get back to a patient. Its own component so the menu's
              query and open state stay out of the topbar. */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
            <PatientInteractionsMenu />
          </Box>

          {/* Quick Actions Overflow Menu - Mobile/Tablet Only */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' } }}>
            <Tooltip title="Quick Actions">
              <IconButton
                onClick={handleQuickActionsOpen}
                sx={{
                  color: 'white',
                  bgcolor: alpha(theme.palette.common.white, 0.1),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.2),
                  },
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={quickActionsAnchor}
              open={Boolean(quickActionsAnchor)}
              onClose={handleQuickActionsClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                },
              }}
            >
              {quickActions.map((action) => (
                <MenuItem
                  key={action.key}
                  onClick={() => {
                    handleQuickActionsClose();
                    action.action();
                  }}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon sx={{ color: 'primary.main' }}>
                    {action.icon}
                  </ListItemIcon>
                  <Typography variant="body2" sx={{ flex: 1 }}>{action.label}</Typography>
                  {action.shortcut && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2, fontFamily: 'monospace' }}>
                      {action.shortcut}
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Notifications intentionally not rendered (GP-1404): the feature is not
              built, and a permanently disabled bell reads as a broken icon in the
              cluster. Restore a real IconButton here when notifications ship. */}

          {/* Pharmacy Selector - now visible on all sizes */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PharmacyContextCard />
          </Box>




          {/* User Menu */}
          <IconButton
            size={isMobile ? 'medium' : 'large'}
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleUserMenuOpen}
            sx={{ ml: { xs: 0.5, sm: 1 } }}
          >
            <Avatar
              src={avatarUrl || user?.picture || undefined}
              sx={{
                width: { xs: 28, sm: 36 },
                height: { xs: 28, sm: 36 },
                bgcolor: alpha(theme.palette.common.white, 0.2),
                border: `2px solid ${alpha(theme.palette.common.white, 0.3)}`,
                fontSize: { xs: '0.75rem', sm: '1rem' },
              }}
            >
              {!avatarUrl && !user?.picture && getUserInitial()}
            </Avatar>
          </IconButton>
        </Box>

        {/* User Menu */}
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleUserMenuClose}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 200,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2">{getUserName()}</Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => { handleUserMenuClose(); navigate('/profile'); }}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => { handleUserMenuClose(); navigate('/settings'); }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
          {isPharmacyAdmin && [
              <Divider key="admin-divider" />,
              <ListSubheader key="admin-header" sx={{ lineHeight: '32px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.disabled', bgcolor: 'transparent' }}>
                Administration
              </ListSubheader>,
              <MenuItem key="admin-hub" onClick={() => { handleUserMenuClose(); navigate('/admin'); }}>
                <ListItemIcon>
                  <AdminPanelSettings fontSize="small" />
                </ListItemIcon>
                Administration Hub
              </MenuItem>,
              <MenuItem key="roles" onClick={() => { handleUserMenuClose(); navigate('/admin/roles'); }}>
                <ListItemIcon>
                  <SecurityIcon fontSize="small" />
                </ListItemIcon>
                Roles & Permissions
              </MenuItem>,
              <MenuItem key="sms-templates" onClick={() => { handleUserMenuClose(); navigate('/admin/sms-templates'); }}>
                <ListItemIcon>
                  <SmsIcon fontSize="small" />
                </ListItemIcon>
                SMS Templates
              </MenuItem>,
              <MenuItem key="prescription-templates" onClick={() => { handleUserMenuClose(); navigate('/admin/prescription-templates'); }}>
                <ListItemIcon>
                  <Description fontSize="small" />
                </ListItemIcon>
                Rx Templates
              </MenuItem>,
              <MenuItem key="system-logs" onClick={() => { handleUserMenuClose(); navigate('/system-logs'); }}>
                <ListItemIcon>
                  <Description fontSize="small" />
                </ListItemIcon>
                System Logs
              </MenuItem>,
              <MenuItem key="drug-catalog" onClick={() => { handleUserMenuClose(); navigate('/drug-catalog'); }}>
                <ListItemIcon>
                  <Inventory fontSize="small" />
                </ListItemIcon>
                Drug Catalog
              </MenuItem>,
              <MenuItem key="reports" onClick={() => { handleUserMenuClose(); navigate('/reports'); }}>
                <ListItemIcon>
                  <AssessmentIcon fontSize="small" />
                </ListItemIcon>
                Reports
              </MenuItem>,
              <ListSubheader key="ops-header" sx={{ lineHeight: '32px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.disabled', bgcolor: 'transparent' }}>
                Operations
              </ListSubheader>,
              <MenuItem key="order-mgmt" onClick={() => { handleUserMenuClose(); navigate('/order-management'); }}>
                <ListItemIcon>
                  <Assignment fontSize="small" />
                </ListItemIcon>
                Order Management
              </MenuItem>,
          ]}
          {/* GP-373: Pharmacy Technicians can access Drug Catalog */}
          {!isPharmacyAdmin && hasCustomRole('pharmacy_technician') && [
              <Divider key="tech-divider" />,
              <MenuItem key="drug-catalog" onClick={() => { handleUserMenuClose(); navigate('/drug-catalog'); }}>
                <ListItemIcon>
                  <Inventory fontSize="small" />
                </ListItemIcon>
                Drug Catalog
              </MenuItem>,
          ]}
          {/* GP-260: Data Analysts can access Reports */}
          {!isPharmacyAdmin && hasCustomRole('data_analyst') && [
              <Divider key="analyst-divider" />,
              <MenuItem key="reports" onClick={() => { handleUserMenuClose(); navigate('/reports'); }}>
                <ListItemIcon>
                  <AssessmentIcon fontSize="small" />
                </ListItemIcon>
                Reports
              </MenuItem>,
          ]}
          {canSeeOperations && [
              !isPharmacyAdmin && <Divider key="ops-divider" />,
              <MenuItem key="delivery-tracking" onClick={() => { handleUserMenuClose(); navigate('/delivery-tracking'); }}>
                <ListItemIcon>
                  <LocalShipping fontSize="small" />
                </ListItemIcon>
                Delivery Tracking
              </MenuItem>,
              !hasCustomRole('onepoint') && !hasCustomRole('onepoint_manager') && (
                <ClinicModuleGate key="clinics">
                  <MenuItem onClick={() => { handleUserMenuClose(); navigate('/clinics'); }}>
                    <ListItemIcon>
                      <BusinessIcon fontSize="small" />
                    </ListItemIcon>
                    Clinic Management
                  </MenuItem>
                </ClinicModuleGate>
              ),
          ]}
          <Divider />
          <MenuItem onClick={() => {
            // Create a Sentry event and show the feedback dialog
            const eventId = Sentry.captureMessage('User initiated bug report', 'info');
            Sentry.showReportDialog({
              eventId: eventId,
              user: {
                email: user?.email || '',
                name: getUserName() || ''
              }
            });
            handleUserMenuClose();
          }}>
            <ListItemIcon>
              <BugReport fontSize="small" />
            </ListItemIcon>
            Report a Bug
          </MenuItem>
          <MenuItem onClick={() => { handleUserMenuClose(); navigate('/help'); }}>
            <ListItemIcon>
              <MenuBookIcon fontSize="small" />
            </ListItemIcon>
            Help Center
          </MenuItem>
          <MenuItem onClick={() => { handleUserMenuClose(); toggle(); }}>
            <ListItemIcon>
              <HelpOutline fontSize="small" />
            </ListItemIcon>
            Chat with Support
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>

        {/* Rx Search Dialog */}
        <RxSearchDialog
          open={rxSearchOpen}
          onClose={() => setRxSearchOpen(false)}
          onSelectRx={handleRxSelect}
        />
      </Toolbar>
    </AppBar>
  );
};
